import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  InputBase,
  ButtonBase,
  Link,
  Select,
  MenuItem,
} from "@mui/material";
import logo from "../assets/legacy grey.png";
import { admin_base_url } from "../Global";
import {
  ROLES,
  ROLE_LABELS,
  ROLE_COLORS,
  ROLE_DESCRIPTIONS,
  SELECTABLE_ROLES,
} from "../adminConfig";

const Register = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    role_type: ROLES.READ_ONLY_ADMIN,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(admin_base_url + "/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          password: form.password,
          role_type: form.role_type,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Registration failed.");
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        style={{
          width: "100%",
          minHeight: "100vh",
          background: "#0f172a",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          style={{
            background: "#1f2937",
            borderRadius: "20px",
            padding: "48px 40px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            textAlign: "center",
            maxWidth: "400px",
          }}
        >
          <Box
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: "rgba(22,163,74,0.2)",
              border: "2px solid #4ade80",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "28px",
            }}
          >
            X
          </Box>
          <Typography
            style={{
              color: "white",
              fontSize: "20px",
              fontWeight: 700,
              fontFamily: "Gilroy, sans-serif",
            }}
          >
            Request Submitted!
          </Typography>
          <Typography
            style={{
              color: "#9ca3af",
              fontSize: "13px",
              fontFamily: "Gilroy, sans-serif",
              lineHeight: 1.6,
            }}
          >
            Your account is pending approval by a Super Admin.
            <br />
            You'll be redirected to login shortly.
          </Typography>
        </Box>
      </Box>
    );
  }

  const selectedColor = ROLE_COLORS[form.role_type] || "#9ca3af";

  return (
    <Box
      style={{
        width: "100%",
        minHeight: "100vh",
        background: "#0f172a",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 16px",
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        style={{
          background: "#1f2937",
          borderRadius: "20px",
          padding: "36px 40px",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          width: "100%",
          maxWidth: "480px",
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Logo"
            style={{ height: "36px", objectFit: "contain" }}
          />
          <Typography
            style={{
              color: "white",
              fontSize: "20px",
              fontWeight: 700,
              fontFamily: "Gilroy, sans-serif",
            }}
          >
            Request Admin Access
          </Typography>
          <Typography
            style={{
              color: "#6b7280",
              fontSize: "13px",
              fontFamily: "Gilroy, sans-serif",
            }}
          >
            Pending Super Admin approval
          </Typography>
        </Box>

        <Box style={{ display: "flex", gap: "12px" }}>
          <Field label="First Name">
            <StyledInput
              value={form.first_name}
              onChange={handleChange("first_name")}
              placeholder="First name"
              required
            />
          </Field>
          <Field label="Last Name">
            <StyledInput
              value={form.last_name}
              onChange={handleChange("last_name")}
              placeholder="Last name"
              required
            />
          </Field>
        </Box>

        <Field label="Email Address">
          <StyledInput
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            placeholder="admin@example.com"
            required
          />
        </Field>

        <Field label="Password">
          <StyledInput
            type="password"
            value={form.password}
            onChange={handleChange("password")}
            placeholder="Min. 8 characters"
            required
          />
        </Field>

        <Field label="Confirm Password">
          <StyledInput
            type="password"
            value={form.confirmPassword}
            onChange={handleChange("confirmPassword")}
            placeholder="Repeat password"
            required
          />
        </Field>

        <Field label="Requested Role">
          <Select
            value={form.role_type}
            onChange={(e) =>
              setForm((p) => ({ ...p, role_type: e.target.value }))
            }
            variant="standard"
            disableUnderline
            sx={{
              background: "#111827",
              borderRadius: "8px",
              padding: "0 14px",
              height: "46px",
              color: selectedColor,
              fontFamily: "Gilroy, sans-serif",
              fontSize: "14px",
              fontWeight: 600,
              border: `1px solid ${selectedColor}44`,
              "& .MuiSelect-icon": { color: selectedColor },
            }}
          >
            {SELECTABLE_ROLES.map((r) => (
              <MenuItem
                key={r}
                value={r}
                sx={{
                  color: ROLE_COLORS[r],
                  fontFamily: "Gilroy, sans-serif",
                  fontWeight: 600,
                }}
              >
                {ROLE_LABELS[r]}
              </MenuItem>
            ))}
          </Select>

          <Typography
            style={{
              color: "#6b7280",
              fontSize: "12px",
              fontFamily: "Gilroy, sans-serif",
              marginTop: "6px",
              lineHeight: 1.5,
            }}
          >
            {ROLE_DESCRIPTIONS[form.role_type]}
          </Typography>
        </Field>

        {error && (
          <Box
            style={{
              borderRadius: "8px",
              padding: "10px 14px",
              background: "rgba(239,68,68,0.1)",
              border: "1px solid rgba(239,68,68,0.3)",
            }}
          >
            <Typography
              style={{
                color: "#f87171",
                fontSize: "13px",
                fontFamily: "Gilroy, sans-serif",
              }}
            >
              {error}
            </Typography>
          </Box>
        )}

        <ButtonBase
          type="submit"
          disabled={loading}
          style={{
            background: loading ? "#374151" : "#FF6B35",
            color: "white",
            height: "46px",
            borderRadius: "8px",
            fontFamily: "Gilroy, sans-serif",
            fontSize: "15px",
            fontWeight: 700,
            transition: "background 0.2s",
          }}
        >
          {loading ? "Submitting…" : "Request Access"}
        </ButtonBase>

        <Box style={{ textAlign: "center" }}>
          <Link
            component="button"
            type="button"
            onClick={() => navigate("/login")}
            style={{
              color: "#6b7280",
              fontSize: "13px",
              fontFamily: "Gilroy, sans-serif",
            }}
          >
            Already have an account? Sign In
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

const Field = ({ label, children }) => (
  <Box
    style={{ display: "flex", flexDirection: "column", gap: "6px", flex: 1 }}
  >
    <Typography
      style={{
        color: "#9ca3af",
        fontSize: "12px",
        fontWeight: 600,
        fontFamily: "Gilroy, sans-serif",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const StyledInput = (props) => (
  <InputBase
    {...props}
    sx={{
      background: "#111827",
      borderRadius: "8px",
      padding: "0 14px",
      height: "46px",
      color: "white",
      fontFamily: "Gilroy, sans-serif",
      fontSize: "14px",
      border: "1px solid #374151",
      "& input::placeholder": { color: "#6b7280" },
      "&.Mui-focused": { border: "1px solid #FF6B35" },
    }}
  />
);

export default Register;
